
import { 
  User, 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { auth, db } from '@/lib/firebase';
import { UserType } from './types';
import { 
  findAhavexerByAhavexId, 
  checkAdminRole, 
  persistUserSession,
  saveUserToFirestore 
} from './authUtils';
import { doc, getDoc } from 'firebase/firestore';

// Login for ahavexers (members)
export const loginMember = async (
  emailOrAhavexId: string, 
  password: string,
  setUserType: (type: UserType) => void
) => {
  // Check if this is the admin email from environment variables
  const adminEmail = import.meta.env.VITE_ADMIN_EMAIL || 'ahavexadmin@gmail.com';
  const isAdminEmail = emailOrAhavexId === adminEmail;
  
  // Check if input is an email (contains @) or an ahavexId
  if (!emailOrAhavexId.includes('@')) {
    // It's likely an ahavexId, so we need to find the corresponding email
    const ahavexerData = await findAhavexerByAhavexId(emailOrAhavexId);
    
    if (!ahavexerData) {
      throw new Error('Ahavexer not found with this ID');
    }
    
    // Use the email from the Firestore document to authenticate
    const result = await signInWithEmailAndPassword(auth, ahavexerData.email, password);
    
    // Check if user has admin role
    const isAdmin = await checkAdminRole(result.user.uid);
    if (isAdmin) {
      setUserType('admin');
      persistUserSession(result.user, 'admin');
    } else {
      setUserType('ahavexer');
      persistUserSession(result.user, 'ahavexer');
    }
    
    return result;
  } else {
    // It's an email. If it's the admin email, we'll let it through without additional checks
    if (isAdminEmail) {
      try {
        const result = await signInWithEmailAndPassword(auth, emailOrAhavexId, password);
        setUserType('admin');
        persistUserSession(result.user, 'admin');
        return result;
      } catch (error) {
        console.error('Admin login error:', error);
        throw error;
      }
    } else {
      // For regular email login, proceed with normal checks
      const result = await signInWithEmailAndPassword(auth, emailOrAhavexId, password);
      
      // Check the user document in Firestore
      const userDoc = await getDoc(doc(db, 'users', result.user.uid));
      
      // If the user doesn't have an ahavexId, it's not an ahavexer account
      if (userDoc.exists()) {
        const userData = userDoc.data();
        if (!userData.ahavexId && !userData.role?.includes('admin')) {
          // Logout the user since they are not an ahavexer
          await signOut(auth);
          throw new Error('This account is not an ahavexer account. Please login as a customer.');
        }
      }
      
      // Check if user has admin role
      const isAdmin = await checkAdminRole(result.user.uid);
      if (isAdmin) {
        setUserType('admin');
        persistUserSession(result.user, 'admin');
      } else {
        setUserType('ahavexer');
        persistUserSession(result.user, 'ahavexer');
      }
      
      return result;
    }
  }
};

// Login for customers
export const loginCustomer = async (
  email: string, 
  password: string,
  setUserType: (type: UserType) => void
) => {
  // Check if this is the admin email - if so, reject it for customer login
  const adminEmail = import.meta.env.VITE_ADMIN_EMAIL || 'ahavexadmin@gmail.com';
  if (email === adminEmail) {
    throw new Error('This is an admin account. Please login as an ahavexer instead.');
  }
  
  const result = await signInWithEmailAndPassword(auth, email, password);
  
  // Check the user document in Firestore
  const userDoc = await getDoc(doc(db, 'users', result.user.uid));
  
  // If the user has an ahavexId, they should not login as a customer
  if (userDoc.exists()) {
    const userData = userDoc.data();
    if (userData.ahavexId || userData.role === 'ahavexer' || userData.role?.includes('admin')) {
      // Logout the user since they should not login as a customer
      await signOut(auth);
      throw new Error('This account is an ahavexer account. Please login as an ahavexer instead.');
    }
  }
  
  setUserType('customer');
  persistUserSession(result.user, 'customer');
  return result;
};

// Login for customers with Google
export const loginCustomerWithGoogle = async (
  setUserType: (type: UserType) => void
) => {
  const provider = new GoogleAuthProvider();
  const result = await signInWithPopup(auth, provider);
  
  // Check if the user already exists in Firestore and has an ahavexId
  const userDoc = await getDoc(doc(db, 'users', result.user.uid));
  
  if (userDoc.exists()) {
    const userData = userDoc.data();
    if (userData.ahavexId || userData.role === 'ahavexer' || userData.role?.includes('admin')) {
      // Logout the user since they should not login as a customer
      await signOut(auth);
      throw new Error('This account is an ahavexer account. Please login as an ahavexer instead.');
    }
  } else {
    // New user, save to Firestore as a customer
    await saveUserToFirestore(result.user.uid, {
      uid: result.user.uid,
      email: result.user.email,
      displayName: result.user.displayName,
      photoURL: result.user.photoURL,
      createdAt: new Date().toISOString(),
      role: 'customer'
    });
  }
  
  setUserType('customer');
  persistUserSession(result.user, 'customer');
  return result;
};

interface AhavexerProfileData {
  fullName: string;
  ahavexId: string;
  address: string;
}

// Register for ahavexers (members)
export const registerMember = async (
  email: string, 
  password: string,
  profileData: AhavexerProfileData,
  setUserType: (type: UserType) => void
) => {
  const result = await createUserWithEmailAndPassword(auth, email, password);
  
  // Save additional profile data to Firestore
  await saveUserToFirestore(result.user.uid, {
    uid: result.user.uid,
    email: email,
    fullName: profileData.fullName,
    ahavexId: profileData.ahavexId,
    address: profileData.address,
    createdAt: new Date().toISOString(),
    role: 'ahavexer'
  });
  
  setUserType('ahavexer');
  persistUserSession(result.user, 'ahavexer');
  return result;
};

// Register for customers
export const registerCustomer = async (
  email: string, 
  password: string,
  setUserType: (type: UserType) => void
) => {
  const result = await createUserWithEmailAndPassword(auth, email, password);
  
  // Save basic user info to Firestore
  await saveUserToFirestore(result.user.uid, {
    uid: result.user.uid,
    email: email,
    createdAt: new Date().toISOString(),
    role: 'customer'
  });
  
  setUserType('customer');
  persistUserSession(result.user, 'customer');
  return result;
};

// Logout
export const logout = async () => {
  await signOut(auth);
  persistUserSession(null, null);
  return;
};
